import { COMPOSE_TONES, type ComposeReference } from '@fieldops/shared'
import { useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { Icon } from '../Icon.js'
import { EcoMark } from '../eco/EcoAvatar.js'
import { EcoAction } from '../eco/EcoButtons.js'
import { Spinner } from '../ui.js'
import type { ComposeAssistant } from './useComposeAssistant.js'
import { useDismiss } from '../overlay.js'

const KIND_LABEL: Record<ComposeReference['kind'], string> = {
  quote: 'Quotes',
  invoice: 'Invoices',
  email: 'Emails',
  sms: 'Texts',
  note: 'Notes',
  booking: 'Bookings',
  report: 'Reports',
  job: 'Past jobs',
}

const SIGNAL_DOT = { waiting: 'bg-ink-900', problem: 'bg-red-600', info: 'bg-ink-400' } as const

/** Eco's frog, the mark every AI control wears (decision 116). */
export function AssistantAvatar() {
  return <EcoMark className="size-7" />
}

/**
 * The right-hand side of the compose window.
 *
 * What it offers is worked out from the job's state before anybody types —
 * a quote gone quiet, a text not answered, a booking to confirm — and a tick
 * writes that message into the body on the left. Nothing is sent from here,
 * and nothing it writes is kept anywhere but the unsent message. Decision 95.
 */
export function AssistantPanel({
  assistant,
  onCollapse,
}: {
  assistant: ComposeAssistant
  /** Absent in the field sheet, which has its own Done. */
  onCollapse?: () => void
}) {
  const { data, suggestions, selected, drafting } = assistant
  const [instruction, setInstruction] = useState('')

  const references = data?.references ?? []
  const byId = new Map(references.map((row) => [row.id, row]))

  const submit = () => {
    if (!instruction.trim() || drafting) return
    assistant.ask(instruction)
    setInstruction('')
  }

  return (
    <section aria-label="Assistant" className="flex h-full min-h-0 flex-col">
      {onCollapse ? (
        <header className="flex items-center gap-2.5 px-5 pt-4 pb-2">
          <AssistantAvatar />
          <h3 className="text-[15px] font-semibold text-ink-900">Assistant</h3>
          <button
            type="button"
            aria-label="Hide the Assistant"
            title="Hide the Assistant"
            className="ml-auto grid size-8 place-items-center rounded-md text-ink-400 hover:bg-ink-100 hover:text-ink-900"
            onClick={onCollapse}
          >
            <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M6 6l6 6-6 6M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </header>
      ) : null}

      {/* The top padding keeps a ticked card's outer ring inside the scroll box when it is first. */}
      <div className={cn('flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 pb-4', onCollapse ? 'pt-1' : 'pt-4')}>
        {data?.signal ? <Signal signal={data.signal} /> : null}

        {suggestions.length > 0 ? (
          <div className="flex flex-col gap-2">
            <ul aria-label="Suggestions" className="flex flex-col gap-2">
              {suggestions.map((suggestion) => {
                const on = selected.includes(suggestion.key)
                return (
                  <li key={suggestion.key}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      disabled={drafting}
                      className={cn(
                        'flex w-full items-start gap-3 rounded-lg bg-white px-3.5 py-3 text-left transition-shadow disabled:cursor-progress',
                        on
                          ? 'shadow-[0_1px_3px_rgb(30_35_32/0.12)] ring-2 ring-ink-900'
                          : 'ring-1 ring-ink-200 hover:ring-ink-400',
                      )}
                      onClick={() => assistant.toggle(suggestion.key)}
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          'mt-0.5 grid size-5 shrink-0 place-items-center rounded-[4px]',
                          on ? 'bg-ink-900 text-white' : 'ring-2 ring-ink-300 ring-inset',
                        )}
                      >
                        {on && drafting ? (
                          <Spinner className="size-3" />
                        ) : on ? (
                          <Icon name="check" className="size-3" />
                        ) : null}
                      </span>
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <span className="text-[14px] font-semibold text-ink-900">{suggestion.title}</span>
                        <span className="text-[13px] leading-snug text-ink-500">{suggestion.detail}</span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
            {selected.length > 1 ? (
              <button
                type="button"
                disabled={drafting}
                className="self-start text-[13px] font-semibold text-brand-700 hover:text-brand-800 disabled:opacity-50"
                onClick={assistant.combine}
              >
                Combine selected
              </button>
            ) : null}
          </div>
        ) : data ? (
          <p className="rounded-lg bg-white px-3.5 py-3 text-[13px] text-ink-500 ring-1 ring-ink-200">
            Nothing is waiting on this one. Tell the Assistant what to write below.
          </p>
        ) : (
          <div className="flex flex-col gap-2" aria-hidden="true">
            <span className="h-16 animate-pulse rounded-lg bg-ink-200/60" />
            <span className="h-16 animate-pulse rounded-lg bg-ink-200/40" />
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <p className="text-[11px] font-semibold tracking-wider text-ink-400 uppercase">Tone</p>
          <div role="radiogroup" aria-label="Tone" className="grid grid-cols-3 gap-0.5 rounded-md bg-ink-100 p-0.5">
            {COMPOSE_TONES.map((option) => {
              const on = assistant.tone === option.key
              return (
                <button
                  key={option.key}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  disabled={drafting}
                  className={cn(
                    'rounded-sm py-1.5 text-[13px] transition-colors disabled:cursor-progress',
                    on
                      ? 'bg-ink-900 font-semibold text-white shadow-sm'
                      : 'font-medium text-ink-600 hover:bg-white hover:text-ink-900',
                  )}
                  onClick={() => {
                    if (!on) assistant.changeTone(option.key)
                  }}
                >
                  {option.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <p className="text-[11px] font-semibold tracking-wider text-ink-400 uppercase">Using</p>
          <ul aria-label="Using" className="flex flex-wrap items-center gap-1.5">
            {assistant.using.map((id) => {
              const reference = byId.get(id)
              if (!reference) return null
              return (
                <li
                  key={id}
                  title={reference.detail ?? undefined}
                  className="group flex items-center gap-1 rounded-md bg-white py-0.5 pr-1 pl-2 text-[12px] text-ink-700 ring-1 ring-ink-200 ring-inset"
                >
                  {reference.label}
                  <button
                    type="button"
                    aria-label={`Stop using ${reference.label}`}
                    className="grid size-4 place-items-center rounded-sm text-ink-300 group-hover:text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                    onClick={() => assistant.removeReference(id)}
                  >
                    <Icon name="close" className="size-3" />
                  </button>
                </li>
              )
            })}
            <li>
              <AddReference
                references={references.filter((row) => !assistant.using.includes(row.id))}
                onAdd={assistant.addReference}
              />
            </li>
          </ul>
        </div>

        {assistant.asks.length > 0 ? (
          <ul aria-label="What you asked" className="mt-auto flex flex-col items-end gap-1.5">
            {assistant.asks.map((ask, index) => (
              <li
                key={`${index}-${ask}`}
                className="max-w-[85%] rounded-lg rounded-br-sm bg-white px-3 py-1.5 text-[13px] text-ink-900 ring-1 ring-ink-300 ring-inset"
              >
                {ask}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="flex flex-col gap-2 px-5 pt-2 pb-4">
        {assistant.error ? (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-800">
            {assistant.error}
          </p>
        ) : null}
        <form
          className="ecb-input"
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
        >
          <textarea
            aria-label="Tell the Assistant what to change"
            rows={1}
            value={instruction}
            placeholder="Tell the Assistant what to change…"
            className="max-h-28 min-h-10 flex-1 resize-none border-0 bg-transparent py-2.5 text-[14px] text-ink-900 outline-none placeholder:text-ink-400"
            onChange={(event) => setInstruction(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                submit()
              }
            }}
          />
          {drafting ? (
            <span className="grid size-10 shrink-0 place-items-center" role="status" aria-label="Writing">
              <Spinner tone="brand" className="size-5" />
            </span>
          ) : (
            <EcoAction badge="spark" type="submit" aria-label="Ask the Assistant" title="Interpret" disabled={!instruction.trim()} />
          )}
        </form>
      </div>
    </section>
  )
}

function Signal({ signal }: { signal: NonNullable<NonNullable<ComposeAssistant['data']>['signal']> }) {
  const [lead, ...rest] = signal.headline.split(' · ')
  const tail = [...rest, ...(signal.tracking ? [signal.tracking.charAt(0).toLowerCase() + signal.tracking.slice(1)] : [])]
  return (
    <p className="flex items-baseline gap-2 text-[13px] text-ink-800">
      <span aria-hidden="true" className={cn('size-2 shrink-0 translate-y-[-1px] rounded-[2px]', SIGNAL_DOT[signal.tone])} />
      <span>
        <span className="font-semibold text-ink-900">{lead}</span>
        {tail.length ? <span className="text-ink-500"> · {tail.join(' · ')}</span> : null}
      </span>
    </p>
  )
}

function AddReference({ references, onAdd }: { references: ComposeReference[]; onAdd: (id: string) => void }) {
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  useDismiss(wrap, open, () => setOpen(false))

  const groups = new Map<ComposeReference['kind'], ComposeReference[]>()
  for (const row of references) groups.set(row.kind, [...(groups.get(row.kind) ?? []), row])

  return (
    <div ref={wrap} className="relative">
      <button
        type="button"
        aria-expanded={open}
        disabled={references.length === 0}
        className="px-1 text-[12px] font-semibold text-brand-700 hover:text-brand-800 disabled:text-ink-300"
        onClick={() => setOpen((was) => !was)}
      >
        + Add
      </button>
      {open ? (
        <div
          role="menu"
          aria-label="Add something for the Assistant to use"
          className="absolute top-full left-0 z-30 mt-1 max-h-80 w-72 overflow-y-auto rounded-lg bg-white p-1 shadow-xl ring-1 ring-ink-200"
        >
          {[...groups.entries()].map(([kind, rows]) => (
            <div key={kind} className="py-1">
              <p className="px-2.5 pt-1 pb-0.5 text-[11px] font-semibold tracking-wide text-ink-400 uppercase">
                {KIND_LABEL[kind]}
              </p>
              {rows.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  role="menuitem"
                  className="flex w-full flex-col rounded-md px-2.5 py-1.5 text-left hover:bg-ink-100"
                  onClick={() => {
                    onAdd(row.id)
                    setOpen(false)
                  }}
                >
                  <span className="text-[13px] font-medium text-ink-900">{row.label}</span>
                  {row.detail ? <span className="truncate text-[12px] text-ink-500">{row.detail}</span> : null}
                </button>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
